/**
 * System-notifications popover body (DNKADM-0047 U2; v2 re-point DNKADM-0062).
 *
 * Renders inside the app-bar bell's `Popover` (the anchor/open-state is wired by
 * `NotificationsBell`, U1). Lists the dink-api admin alerts (handoff §5.20) as one
 * row per problem — severity-styled, a severity-driven icon, the scrubbed `message`
 * + `entityRef`, an occurrence/last-seen meta line, and a status chip — with a
 * status filter (Actionable default / Resolved / All) and per-row Acknowledge +
 * Resolve.
 *
 * The **All** scope has no server equivalent (`status=all` → `400 INVALID_STATUS`),
 * so it is composed CLIENT-SIDE from the actionable + RESOLVED calls, de-duplicated
 * by `id` (DNKADM-0243). The list is ONE bounded page; when the server's total
 * exceeds the rendered rows a muted hint says so — there are no paging controls.
 *
 * Data: the popover owns its own filtered list (it re-fetches on open, on a filter
 * change, and after an acknowledge/resolve). The bell's badge keeps reading the
 * shared `useNotifications()` polling context, so after an acknowledge/resolve we
 * ALSO call the context's `refresh()` to keep the badge in sync from that one
 * source of truth.
 *
 * Acknowledge ("I've seen this" — NOT a fix) and Resolve ("the problem is handled")
 * are gated to Configuration mode (`!frontDeskView`): an act-of-record gate, NOT a
 * security control (the endpoint is the OWNER/ADMIN authority — the front-desk
 * session is still the admin's). Resolve DEGRADES GRACEFULLY — if the backend
 * contract omits the resolve endpoint the action surfaces an error toast and the
 * row is otherwise untouched (handoff §5.20).
 *
 * `type` is OPEN-ENDED (new producers add codes without migration) — the row is
 * rendered GENERICALLY BY `severity`, never by a hard-coded `type` set; the raw
 * code is never shown. `message`/`entityRef` are scrubbed through `scrubBrand` at
 * this render boundary (white-label defense-in-depth).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useInRouterContext } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Link as MuiLink,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  acknowledgeNotification,
  getNotifications,
  resolveNotification,
  type Notification,
  type NotificationPage,
  type NotificationSeverity,
  type NotificationStatus,
} from '@/api/notifications';
import { ApiError } from '@/api/errors';
import { useFrontDeskView } from '@/auth/frontDeskHooks';
import { useToast } from '@/components/useToast';
import { Icon } from '@/theme';
import { scrubBrand } from '@/utils/whitelabel';
import { formatRelativeTime } from '@/utils/relativeTime';
import { useNotifications } from './useNotifications';
import { LinkSubscriptionDialog } from '@/features/members/LinkSubscriptionDialog';

/**
 * The popover's filter scopes — a UI concept, NOT wire values. `'actionable'`
 * (default) omits the API `status`; `'all'` has NO server equivalent
 * (`status=all` → `400 INVALID_STATUS`) and is composed client-side below.
 */
type FilterScope = 'actionable' | 'RESOLVED' | 'all';

/** Known notification types that get a fixed staff-facing headline (DNKADM-0442). */
const SIGNUP_SUPERSEDED = 'PAYAPI_SIGNUP_SUPERSEDED';
const SIGNUP_SUPERSEDED_LABEL = 'Payment taken, membership not applied';

/**
 * Severity → leading-accent color (ERROR red / WARN amber / INFO + null neutral).
 * Returns an MUI palette color path usable in `sx`.
 */
function severityColor(severity: NotificationSeverity | null): string {
  if (severity === 'ERROR') return 'error.main';
  if (severity === 'WARN') return 'warning.main';
  return 'text.disabled';
}

/**
 * `severity` → Material Symbols glyph. The notification `type` is OPEN-ENDED (new
 * producers add codes without migration), so the icon is driven GENERICALLY BY
 * SEVERITY — never a hard-coded `type` set (handoff §5.20). An unknown/absent
 * severity degrades to a neutral info glyph; never throws.
 */
function severityIcon(severity: NotificationSeverity | null): string {
  if (severity === 'ERROR') return 'error';
  if (severity === 'WARN') return 'warning';
  return 'info';
}

/** Status → chip label + MUI color. RESOLVED reads success; OPEN/ACK are neutral-ish. */
function statusChip(status: NotificationStatus | null): {
  label: string;
  color: 'default' | 'warning' | 'success';
} {
  if (status === 'OPEN') return { label: 'Open', color: 'warning' };
  if (status === 'ACKNOWLEDGED') return { label: 'Acknowledged', color: 'default' };
  if (status === 'RESOLVED') return { label: 'Resolved', color: 'success' };
  return { label: 'Unknown', color: 'default' };
}

/** Sort weight so ERROR/OPEN floats to the top, then OPEN, then the rest. */
function sortRank(n: Notification): number {
  if (n.status === 'OPEN' && n.severity === 'ERROR') return 0;
  if (n.status === 'OPEN') return 1;
  return 2;
}

/** Newest-`lastSeenAt`-first within a rank bucket; unparseable instants sort last. */
function lastSeenMs(n: Notification): number {
  if (n.lastSeenAt === null) return -Infinity;
  const ms = Date.parse(n.lastSeenAt);
  return Number.isFinite(ms) ? ms : -Infinity;
}

/** ERROR/OPEN first, then OPEN, then by `lastSeenAt` desc. Pure; returns a new array. */
function sortNotifications(list: Notification[]): Notification[] {
  return [...list].sort((a, b) => {
    const rank = sortRank(a) - sortRank(b);
    if (rank !== 0) return rank;
    return lastSeenMs(b) - lastSeenMs(a);
  });
}

/**
 * Merge the two pages the **All** scope is composed from, de-duplicating by `id`
 * and keeping the FIRST occurrence. De-duplication is required, not defensive: it
 * keeps All correct whether the server's no-`status` default means "actionable"
 * or "everything". Rows with a null `id` (malformed) cannot be keyed, so they are
 * all kept rather than collapsed into one.
 *
 * The total is the sum of the two, minus the duplicates we actually removed, so
 * the truncation hint stays honest under either reading of the default. It is
 * null unless BOTH pages carried a total — a partly-unknown total would render a
 * misleading "of N".
 */
function mergePages(first: NotificationPage, second: NotificationPage): NotificationPage {
  const seen = new Set<number>();
  const items: Notification[] = [];
  let duplicates = 0;
  for (const n of [...first.items, ...second.items]) {
    if (n.id !== null) {
      if (seen.has(n.id)) {
        duplicates += 1;
        continue;
      }
      seen.add(n.id);
    }
    items.push(n);
  }
  const total =
    first.total !== null && second.total !== null ? first.total + second.total - duplicates : null;
  return { items, total };
}

/**
 * Fetch one scope. **All** issues the two valid calls CONCURRENTLY and merges
 * them; if EITHER rejects the whole scope rejects, so the panel shows its error
 * treatment rather than a partial list presented as complete.
 */
function fetchScope(scope: FilterScope): Promise<NotificationPage> {
  if (scope === 'RESOLVED') return getNotifications('RESOLVED');
  // 'actionable' → omit the param; the server applies OPEN + ACKNOWLEDGED.
  if (scope === 'actionable') return getNotifications();
  return Promise.all([getNotifications(), getNotifications('RESOLVED')]).then(([a, b]) =>
    mergePages(a, b),
  );
}

type LoadState = 'loading' | 'ready' | 'error';

export function NotificationsPanel({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { frontDeskView } = useFrontDeskView();
  const { refresh: refreshBadge } = useNotifications();
  const toast = useToast();

  const [scope, setScope] = useState<FilterScope>('actionable');
  const [list, setList] = useState<Notification[]>([]);
  // The server's total behind the page; null when it sent none (→ no hint).
  const [total, setTotal] = useState<number | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [ackingId, setAckingId] = useState<number | null>(null);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [linkTarget, setLinkTarget] = useState<{ contactId: string; subscriptionId: string } | null>(
    null,
  );

  // Last-call-wins + unmount guards (mirror NotificationsContext / LocationContext).
  const mountedRef = useRef(true);
  const runIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback((forScope: FilterScope) => {
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    setState('loading');
    void (async () => {
      try {
        const page = await fetchScope(forScope);
        if (!mountedRef.current || runIdRef.current !== runId) return;
        setList(page.items);
        setTotal(page.total);
        setState('ready');
      } catch {
        if (!mountedRef.current || runIdRef.current !== runId) return;
        setState('error');
      }
    })();
  }, []);

  // Load on mount and whenever the filter scope changes.
  useEffect(() => {
    load(scope);
  }, [load, scope]);

  const sorted = useMemo(() => sortNotifications(list), [list]);

  const handleAcknowledge = useCallback(
    (id: number) => {
      setAckingId(id);
      void (async () => {
        try {
          const updated = await acknowledgeNotification(id);
          if (mountedRef.current) {
            // Reflect the server row in place (now ACKNOWLEDGED), then refresh the
            // shared badge so the bell count stays truthful.
            setList((prev) => prev.map((n) => (n.id === id ? updated : n)));
          }
          refreshBadge();
        } catch (err) {
          if (err instanceof ApiError && err.code === 'NOT_FOUND') {
            // Stale/foreign id — the row is gone; tell the user and re-fetch the list.
            toast('Notification not found — refreshing', 'error');
            load(scope);
            refreshBadge();
          } else {
            toast('Could not acknowledge — please try again', 'error');
          }
        } finally {
          if (mountedRef.current) setAckingId(null);
        }
      })();
    },
    [load, scope, refreshBadge, toast],
  );

  // Resolve ("the problem is handled") — the v2 inbox action (handoff §5.20).
  // DEGRADES GRACEFULLY: if the backend omits the resolve endpoint the call throws
  // and we surface an error toast; the row is otherwise untouched.
  const handleResolve = useCallback(
    (id: number) => {
      setResolvingId(id);
      void (async () => {
        try {
          const updated = await resolveNotification(id);
          if (mountedRef.current) {
            setList((prev) => prev.map((n) => (n.id === id ? updated : n)));
          }
          refreshBadge();
        } catch (err) {
          if (err instanceof ApiError && err.code === 'NOT_FOUND') {
            toast('Notification not found — refreshing', 'error');
            load(scope);
            refreshBadge();
          } else {
            toast('Could not resolve — please try again', 'error');
          }
        } finally {
          if (mountedRef.current) setResolvingId(null);
        }
      })();
    },
    [load, scope, refreshBadge, toast],
  );

  return (
    <Box sx={{ width: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          pt: 1.5,
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Notifications</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(_e, next: FilterScope | null) => {
            if (next !== null) setScope(next);
          }}
          aria-label="Filter notifications"
        >
          <ToggleButton value="actionable" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
            Actionable
          </ToggleButton>
          <ToggleButton value="RESOLVED" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
            Resolved
          </ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
            All
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {frontDeskView ? (
        <Typography sx={{ px: 2, pb: 1, fontSize: 12, color: 'text.secondary' }}>
          Acknowledging is available in the full view.
        </Typography>
      ) : null}

      <Divider />

      <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
        {state === 'loading' ? (
          <PanelSkeleton />
        ) : state === 'error' ? (
          <PanelError onRetry={() => load(scope)} />
        ) : sorted.length === 0 ? (
          <PanelEmpty scope={scope} />
        ) : (
          sorted.map((n, i) => (
            <Box key={n.id ?? `row-${i}`}>
              {i > 0 ? <Divider component="div" /> : null}
              <NotificationRow
                notification={n}
                canActOnIt={!frontDeskView}
                acking={ackingId === n.id}
                resolving={resolvingId === n.id}
                onAcknowledge={handleAcknowledge}
                onResolve={handleResolve}
                onLinkSubscription={setLinkTarget}
                onNavigate={onNavigate}
              />
            </Box>
          ))
        )}
      </Box>

      {/* Truncation hint — the page is bounded, so say so when the server's total
          exceeds what we rendered. A hint only: there are no paging controls. */}
      {state === 'ready' && sorted.length > 0 && total !== null && total > sorted.length ? (
        <Typography sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary' }}>
          Showing {sorted.length} of {total} notifications.
        </Typography>
      ) : null}

      {linkTarget !== null && (
        <LinkSubscriptionDialog
          contactId={linkTarget.contactId}
          subscriptionId={linkTarget.subscriptionId}
          open
          onClose={() => setLinkTarget(null)}
          onLinked={() => {
            setLinkTarget(null);
            toast('Subscription linked.');
            load(scope);
            refreshBadge();
          }}
        />
      )}
    </Box>
  );
}

interface NotificationRowProps {
  notification: Notification;
  /** Configuration mode only — gates the Acknowledge/Resolve buttons (act-of-record). */
  canActOnIt: boolean;
  acking: boolean;
  resolving: boolean;
  onAcknowledge: (id: number) => void;
  onResolve: (id: number) => void;
  onLinkSubscription: (target: { contactId: string; subscriptionId: string }) => void;
  /** Called when a row link navigates away, so the popover can close. */
  onNavigate: (() => void) | undefined;
}

function NotificationRow({
  notification: n,
  canActOnIt,
  acking,
  resolving,
  onAcknowledge,
  onResolve,
  onLinkSubscription,
  onNavigate,
}: NotificationRowProps) {
  const message = scrubBrand(n.message);
  const entityRef = scrubBrand(n.entityRef);
  const chip = statusChip(n.status);
  const relative = formatRelativeTime(n.lastSeenAt);
  const rowIcon = n.type === 'PAYAPI_SUBSCRIPTION_UNMATCHED' ? 'link' : severityIcon(n.severity);
  // Acknowledge/Resolve need a real id and Configuration mode; Acknowledge applies
  // while OPEN, Resolve applies until the row is already RESOLVED.
  const id = n.id;
  const showAck = canActOnIt && n.status === 'OPEN' && id !== null;
  const showResolve =
    canActOnIt && n.status !== 'RESOLVED' && id !== null && n.type !== 'PAYAPI_SUBSCRIPTION_UNMATCHED';
  // PAYAPI_SIGNUP_SUPERSEDED (DNKAPI-0634): `entityRef` is the PAYMENT id. The ledger has no
  // payment-id filter, so the link opens the Payments tab (narrowed to the member when the row
  // carries one) and the payment id stays visible on the row.
  const superseded = n.type === SIGNUP_SUPERSEDED;
  const paymentLink =
    superseded
      ? `/transaction-ledger?tab=payments${
          n.contactId === null ? '' : `&contactId=${encodeURIComponent(n.contactId)}`
        }`
      : null;
  const linkableSubscription =
    n.type === 'PAYAPI_SUBSCRIPTION_UNMATCHED' && n.entityRef !== null && n.contactId !== null;
  const busy = acking || resolving;

  return (
    <Box sx={{ display: 'flex', gap: 1.25, px: 2, py: 1.5 }}>
      {/* Severity-driven icon — the OPEN-ENDED `type` is never hard-coded here. */}
      <Icon
        name={rowIcon}
        sx={{ fontSize: 22, color: severityColor(n.severity), mt: '2px' }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
            {n.type === 'PAYAPI_SUBSCRIPTION_UNMATCHED'
              ? 'Unmatched payment subscription'
              : superseded
                ? SIGNUP_SUPERSEDED_LABEL
                : (message ?? 'A system notification (no description provided).')}
          </Typography>
          <Chip size="small" label={chip.label} color={chip.color} sx={{ flexShrink: 0 }} />
        </Box>

        {superseded && message !== null ? (
          <Typography sx={{ mt: 0.25, fontSize: 12, color: 'text.secondary' }}>{message}</Typography>
        ) : null}

        {entityRef !== null ? (
          <Typography sx={{ mt: 0.25, fontSize: 12, color: 'text.secondary' }}>
            {entityRef}
          </Typography>
        ) : null}

        <Typography sx={{ mt: 0.5, fontSize: 11.5, color: 'text.disabled' }}>
          seen {n.occurrenceCount}×{relative !== null ? `, last ${relative}` : ''}
        </Typography>

        {paymentLink !== null ? <PaymentLink to={paymentLink} onNavigate={onNavigate} /> : null}

        {(showAck || showResolve || linkableSubscription) && id !== null ? (
          <Stack direction="row" sx={{ mt: 1, gap: 1 }}>
            {linkableSubscription ? (
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                onClick={() => {
                  if (n.entityRef !== null && n.contactId !== null) {
                    onLinkSubscription({ contactId: n.contactId, subscriptionId: n.entityRef });
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                Link subscription
              </Button>
            ) : null}
            {showAck ? (
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => onAcknowledge(id)}
                sx={{ textTransform: 'none' }}
              >
                {acking ? 'Acknowledging…' : 'Acknowledge'}
              </Button>
            ) : null}
            {showResolve ? (
              <Button
                size="small"
                variant="outlined"
                color="success"
                disabled={busy}
                onClick={() => onResolve(id)}
                sx={{ textTransform: 'none' }}
              >
                {resolving ? 'Resolving…' : 'Resolve'}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}

/** "View payment": a router link inside the app shell, a plain href when rendered bare. */
function PaymentLink({ to, onNavigate }: { to: string; onNavigate: (() => void) | undefined }) {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <MuiLink component={RouterLink} to={to} onClick={onNavigate} sx={{ mt: 0.5, display: 'inline-block', fontSize: 12.5 }}>
      View payment
    </MuiLink>
  ) : (
    <MuiLink href={to} onClick={onNavigate} sx={{ mt: 0.5, display: 'inline-block', fontSize: 12.5 }}>
      View payment
    </MuiLink>
  );
}

/** Skeleton rows while the list loads. */
function PanelSkeleton() {
  return (
    <Box aria-busy="true" aria-label="Loading notifications">
      {[0, 1, 2].map((i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1.25, px: 2, py: 1.5 }}>
          <Skeleton variant="circular" width={22} height={22} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="85%" />
            <Skeleton variant="text" width="40%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Filter-aware empty copy. */
function PanelEmpty({ scope }: { scope: FilterScope }) {
  const text =
    scope === 'RESOLVED'
      ? 'Nothing resolved yet.'
      : scope === 'all'
        ? 'No notifications yet.'
        : "No notifications — you're all clear.";
  return (
    <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
      <Icon name="check_circle" sx={{ fontSize: 28, color: 'text.disabled' }} />
      <Typography sx={{ mt: 1, fontSize: 13, color: 'text.secondary' }}>{text}</Typography>
    </Box>
  );
}

/** Error state with a Retry. */
function PanelError({ onRetry }: { onRetry: () => void }) {
  return (
    <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
        Couldn&apos;t load notifications.
      </Typography>
      <Button size="small" variant="outlined" onClick={onRetry} sx={{ mt: 1.5, textTransform: 'none' }}>
        Retry
      </Button>
    </Box>
  );
}
